/**
 * @fileoverview Standard, reusable help content for every data grid in the app.
 * @module ui-admin/help/gridHelpContent
 *
 * Responsibilities: - Define the shared, illustrated help sections describing the generic grid
 *                     functionality common to all grids: search, filters, advanced queries,
 *                     column settings, saved grid templates, refresh, bulk operations
 *                     (double-click multi-select), and the detail slide-in.
 * Dependencies:     - ./types (GenericHelpSection, HelpBlock)
 *                   - ./gridHelpIllustrations (GridHelpIllustrationId)
 * Architecture:     - Pure static data bundled with the app (local-first by construction —
 *                     available offline, no API/Dexie). Rendered by the shared HelpPanel and
 *                     wired into GridPageShell so every grid exposes the same Help button.
 * Notes:            - Authored as plain HelpBlock data (never JSX). Keep wording grid-agnostic;
 *                     grid-specific guidance belongs in that grid's own page, not here.
 */

import type { GenericHelpSection } from './types';
import type { GridHelpIllustrationId } from './gridHelpIllustrations';

/** Stable identifier for a standard grid-help section (ToC navigation + deep links). */
export type GridHelpSectionId =
  | 'overview'
  | 'searchFilters'
  | 'queries'
  | 'columns'
  | 'templates'
  | 'refresh'
  | 'bulkActions'
  | 'detailSlideIn';

/** A standard grid-help section specialised to the grid section/illustration id unions. */
export type GridHelpSection = GenericHelpSection<GridHelpSectionId, GridHelpIllustrationId>;

/**
 * @reusable
 * Usage: pass as the `sections` prop to HelpPanel (default content for GridPageShell's Help
 *        button). Pair with GRID_HELP_ILLUSTRATIONS for the matching figures.
 * Rules: static, presentation-only data — grid-agnostic; no per-grid specifics.
 */
export const GRID_HELP_SECTIONS: GridHelpSection[] = [
  {
    id: 'overview',
    title: 'Working with grids',
    summary: 'How every grid in the app is laid out and what you can do with it.',
    illustration: 'gridOverview',
    body: [
      {
        type: 'paragraph',
        text:
          'Every list in the app uses the same grid. The toolbar above the rows holds search, ' +
          'filters, the advanced query builder, column settings, and bulk actions. The header ' +
          'has buttons for saved grid templates and refresh. Rows show your records; clicking a ' +
          'row opens a detail slide-in on the right.',
      },
      {
        type: 'paragraph',
        text:
          'Your layout choices — visible columns, order, sorting, grouping, filters, and search — ' +
          'are remembered for the session and can be saved as reusable grid templates.',
      },
      {
        type: 'tip',
        text:
          'Everything here works offline. Grids read from your local data, so search, filter, ' +
          'sort, and grouping stay instant even with no connection.',
      },
    ],
  },
  {
    id: 'searchFilters',
    title: 'Search & filters',
    summary: 'Narrow the list quickly with the search box and per-column filters.',
    illustration: 'filtersQueries',
    body: [
      {
        type: 'steps',
        items: [
          'Type in the search box to match across the visible columns instantly.',
          'Click Filters to add per-column conditions (status, type, dates, and more).',
          'Active filters appear as chips — click a chip to edit it or remove it.',
          'Use “Clear filters” in the empty state to reset everything at once.',
        ],
      },
      {
        type: 'tip',
        text: 'Search and filters combine — results match the search text AND every active filter.',
      },
    ],
  },
  {
    id: 'queries',
    title: 'Advanced queries',
    summary: 'Build precise multi-condition queries with AND / OR logic.',
    illustration: 'filtersQueries',
    body: [
      {
        type: 'paragraph',
        text:
          'The advanced query builder lets you combine several conditions on any column using ' +
          'AND or OR logic — for example “status is Published OR document type is Permit”. It ' +
          'goes beyond the quick filters when you need exact, repeatable criteria.',
      },
      {
        type: 'steps',
        items: [
          'Open the query builder from the toolbar (the lightning-bolt button).',
          'Add one condition per rule: pick a column, an operator, and a value.',
          'Choose AND (all must match) or OR (any can match) for the group.',
          'Apply the query — matching rows stay; save it as a grid template to reuse it.',
        ],
      },
    ],
  },
  {
    id: 'columns',
    title: 'Column settings',
    summary: 'Show, hide, reorder, sort, and group columns.',
    illustration: 'columnsTemplates',
    body: [
      {
        type: 'steps',
        items: [
          'Open Columns to toggle which columns are visible.',
          'Drag columns to reorder them; click a header to sort ascending/descending.',
          'Group by one or more columns to roll rows up into collapsible sections.',
        ],
      },
      {
        type: 'tip',
        text: 'Hide columns you rarely use to keep the grid readable, then save the layout as a template.',
      },
    ],
  },
  {
    id: 'templates',
    title: 'Grid templates',
    summary: 'Save and switch between named grid layouts.',
    illustration: 'columnsTemplates',
    body: [
      {
        type: 'paragraph',
        text:
          'A grid template captures your current view — visible columns, order, sorting, ' +
          'grouping, filters, and search — under a name you choose. Switch templates to jump ' +
          'between purpose-built views (e.g. “Published only”, “Needs review”). Click Apply to ' +
          'switch now. The star only marks a default for later visits. Update overwrites that ' +
          'named layout after you confirm.',
      },
      {
        type: 'steps',
        items: [
          'Use Column Settings to pick the fields you want, then open Templates in the header.',
          'Type a name and click Save (or Update on an existing row) to store the current layout.',
          'Click Apply on that named row to switch. Reset to defaults restores the original factory columns.',
        ],
      },
    ],
  },
  {
    id: 'refresh',
    title: 'Refresh',
    summary: 'Pull the latest records and re-apply your view.',
    body: [
      {
        type: 'paragraph',
        text:
          'Refresh re-reads the grid from your local data and re-applies your current layout. ' +
          'Because data syncs in the background, the grid usually updates on its own — use ' +
          'Refresh when you want to force an immediate re-read.',
      },
    ],
  },
  {
    id: 'bulkActions',
    title: 'Bulk operations',
    summary: 'Select multiple rows and act on them at once.',
    illustration: 'bulkOps',
    body: [
      {
        type: 'steps',
        items: [
          'Double-click a row to enter multi-select mode and select it.',
          'Tick the checkboxes (or the header checkbox) to add more rows.',
          'Choose a bulk action from the toolbar to apply it to every selected row.',
        ],
      },
      {
        type: 'tip',
        text: 'A single click opens a row’s detail slide-in; a double-click starts multi-select for bulk actions.',
      },
    ],
  },
  {
    id: 'detailSlideIn',
    title: 'Detail slide-in',
    summary: 'Inspect a record in a side panel without leaving the grid.',
    illustration: 'detailSlideIn',
    body: [
      {
        type: 'paragraph',
        text:
          'Clicking a row slides in a detail panel on the right with more information about that ' +
          'record, organised into tabs. The grid stays visible behind it, so you can click ' +
          'another row to inspect it next.',
      },
      {
        type: 'steps',
        items: [
          'Click a row to open its detail slide-in.',
          'Switch tabs to see different facets (e.g. preview, setup, metadata, audit log).',
          'Drag the left edge of the panel to resize it; use the expand icon to open the full page.',
          'Close the panel with its × button or the Escape key.',
        ],
      },
      {
        type: 'tip',
        text: 'The Audit Log tab (where available) lists every change made to the record, newest first.',
      },
    ],
  },
];
